import { useCallback, useEffect, useState } from 'react';

import { apiHost } from '../configs/environments';
import type { UserProfile } from '../types/userProfile';
import { setErrorVitalsUser } from '../utils/errorVitals';
import { logError } from '../utils/logging';

export type UseFetchUserProfileResult = {
  loading: boolean;
  error: Error | null;
  userProfile: UserProfile | null;
  getUserProfile: () => Promise<void>;
};

export function useFetchUserProfile(): UseFetchUserProfileResult {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [fetchTimes, setFetchTimes] = useState(0);
  const endpointUrl = `${apiHost}/user`;

  useEffect(() => {
    let isCancelled = false;
    (async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(endpointUrl, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch user: ${response.status}`);
        }

        if (!isCancelled) {
          const data = await response.json();
          const profile = (data.data ?? data) as UserProfile;
          setUserProfile(profile);

          if (profile?.id) {
            setErrorVitalsUser(profile.id);
          }
        }
      } catch (err) {
        if (!isCancelled) {
          logError(err as Error);
          setError(err as Error);
        }
      }
      if (!isCancelled) {
        setLoading(false);
      }
    })();
    // the following does async cleanup for the fetch above
    // (so we don't get "state change on unmounted component" errors)
    return () => {
      isCancelled = true;
    };
  }, [endpointUrl, fetchTimes]);

  const getUserProfile = useCallback(async () => {
    setFetchTimes((currValue) => currValue + 1);
  }, []);

  return { loading, error, userProfile, getUserProfile };
}
